//third-party
import { Row, Col, Container, Badge, Button as BsButton, Form, Alert } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useLoaderData, useNavigate } from "react-router-dom";

//spotsync
import { Plan } from "./Data/ApiTypes";
import { Input } from "./Controls/Input";
import { Select } from "./Controls/Select";
import { MediumPage } from "./Controls/MediumPage";
import { PageTitle } from "./Controls/PageTitle";
import { useParamIds } from "./Data/Common";
import { Button } from "./Controls/Button";
import { showMoney } from "./Data/Money";
import { useRef, useMemo, useState } from "react";
import { Calendar, CalendarX } from "react-bootstrap-icons";
import { MonitorFacilityApiApi, ServerTransferMonitorPlanUpdateReq } from "./shared/api/axios-client";
import { createApiService } from "./shared/apiConfig";
import { HtmlEditor } from "./Controls/HtmlEditor";


const monitorApi = createApiService(MonitorFacilityApiApi)

export function FacilityPlanEditPage() {
    const nav = useNavigate();
    const [loading, setLoading] = useState( false );
    const [msg, setMsg] = useState<string>();
    const { facilityId, planId } = useParamIds();
    const plan   = useLoaderData() as Plan;
    const prices = plan.prices;
    const initialValues = useRef( {
        planEntryId: plan.planEntryId,
        name: plan.name ?? "",
        description: plan.description ?? "",
        accessHours: plan.accessHours ?? "",
        planPriceId: plan.planPriceId ?? null as number | null,
        dayOfMonthInvoice: plan.dayOfMonthInvoice,
        dayOfMonthDue: plan.dayOfMonthDue,
        dayOfMonthLate: plan.dayOfMonthLate,
        note: plan.note ?? "",
        prorate: plan.prorate ?? "",
        terms: plan.terms ?? "",
        approvalEmailText: plan.approvalEmailText ?? "",
        renewalFailureDeactivateAfterDays: plan.renewalFailureDeactivateAfterDays
    } as ServerTransferMonitorPlanUpdateReq & { planPriceId: number | null } );

    const { register, handleSubmit, control, watch, setValue, clearErrors, formState: { errors } } = useForm<ServerTransferMonitorPlanUpdateReq>( {
        defaultValues: initialValues.current
    } );

    const isRenewalEnabled = watch("renewalFailureDeactivateAfterDays") > 0;

    const currentValues = watch();
    const stringKeys = [ "name", "description", "accessHours", "note", "prorate" ] as const;
    const num = (x: number | null | undefined) =>
  x ?? null;

    const hasChanges = useMemo( () => {
        const a = initialValues.current;
        const b = currentValues;
        const str = (x: string | null | undefined ) => ( x ?? "" ).trim();
        const html = (x: string | null | undefined ) => ( x ?? "" ).replace( /<[^>]*>/g, " " ).replace( /\s+/g, " " ).trim();
        const priceId = (x: number | string | null | undefined ) => ( x === "" || x == null ? null : Number( x ) );
        return priceId( a.planPriceId ) !== priceId( b.planPriceId ) ||
            stringKeys.some( ( k ) => str( a[k] ) !== str( b[k] ) ) ||
            html( a.approvalEmailText ) !== html( b.approvalEmailText ) ||
            num(a.renewalFailureDeactivateAfterDays) !==  num(b.renewalFailureDeactivateAfterDays);
    }, [ currentValues ] );

    const onSubmit = async ( data: ServerTransferMonitorPlanUpdateReq ) => {
        setLoading( true );
        if (!isRenewalEnabled) data.renewalFailureDeactivateAfterDays = 0;
        monitorApi.updatePlan({
            planId: planId,
            facilityId: facilityId,
            serverTransferMonitorPlanUpdateReq: data
        }).then((res)=>{
            setMsg( "Plan Updated Successfully" );
            nav( `/facility/${facilityId}/plan` );
        }).catch((err)=>{
            console.log(err)
            //TODO: move to toast
        }).finally(()=>{
             setLoading( false );
        })
    };

    return <MediumPage>
        <div className="d-flex align-items-center gap-3">
            <BsButton variant="link" className="p-0" onClick={() => nav(-1)}>  ← Back</BsButton>
            <PageTitle>
                Edit Plan "{plan.name}"
            
            </PageTitle>
        </div>
        {plan.isPrivate && (
            <Badge className="fs-6 mb-2">Private Plan</Badge>
        )}
        <form onSubmit={handleSubmit( onSubmit )}>
            <Input
                type="text"
                label="Name"
                {...register( "name", { required: "Name is required" } )}
                error={errors.name}
            />

            <div className="two-col">
            <Input
                type="text"
                label="Description"
                as="textarea"
                rows={2}
                maxLength={256}
                {...register("description", { required: "Description is required" })}
                error={errors.description}
            />
            </div>

            <Row className="mb-3">
            <Col md={4}>
                <Form.Label className="mb-1">Terms and Conditions</Form.Label>
            </Col>
            <Col md={9}>
                <Controller
                name="terms"
                control={control}
                rules={{ required: "Terms and Conditions is required" }}
                render={({ field }) => (
                    <HtmlEditor
                    value={field.value || ""}
                    onChange={field.onChange}
                    placeholder="Enter terms and conditions..."
                    isInvalid={!!errors.terms}
                    />
                )}
                />
                {errors.terms && (
                <Form.Text className="d-block text-danger ms-1">
                    {errors.terms.message}
                </Form.Text>
                )}
            </Col>
            </Row>

            <Row className="mb-3">
            <Col md={12}>
                <Form.Label className="mb-1">Approval Email Text</Form.Label>
                <div className="text-muted small">
                Add an optional message that will appear in the email parkers receive when their subscription is approved.
                </div>
            </Col>
            <Col md={9}>
                <Controller
                name="approvalEmailText"
                control={control}
                render={({ field }) => (
                    <HtmlEditor
                    value={field.value || ""}
                    onChange={field.onChange}
                    placeholder="Enter the approval email text..."
                    />
                )}
                />
            </Col>
            </Row>

            <Input
                type="text"
                label="Access Hours"
                placeholder="Mon - Fri 7am - 7pm"
                maxLength={128}
                {...register( "accessHours", { required: "Access hours are required" } )}
                error={errors.accessHours}
            />

            <Container fluid className="g-0">
                <Row>
                    <Col className="fw-bold h4">
                        Scheduling
                        <p className="text-secondary fs-6 fw-normal">
                            Choose the day of the month that invoices are generated, payment is due, and late fees are accessed
                        </p>
                    </Col>
                </Row>
                <Row>
                    <Col xs={3}>
                        <Input
                            type="number"
                            label="Send Invoices"
                            icon={<Calendar />}
                            min={1} max={28}
                            disabled
                            {...register( "dayOfMonthInvoice" )}
                            error={errors.dayOfMonthInvoice}
                        />
                    </Col>
                    <Col xs={3}>
                        <Input
                            type="number"
                            label="Payment Due"
                            icon={<Calendar />}
                            min={1} max={28}
                            disabled
                            {...register( "dayOfMonthDue" )}
                            error={errors.dayOfMonthDue}
                        />
                    </Col>
                    <Col xs={3}>
                        <Input
                            type="number"
                            label="Late Fee Day"
                            icon={<CalendarX />}
                            min={1} max={28}
                            disabled
                            {...register( "dayOfMonthLate" )}
                            error={errors.dayOfMonthLate}
                        />
                    </Col>
                    <Col xs={3}>
                        <Select
                            label="Prorate"
                            explanation="Proration schema"
                            required
                            min={1} max={28}
                            {...register( "prorate")}
                            error={errors.prorate}
                            disabled
                        >
                            <option value={'Daily'}>Daily</option>
                            <option value={'Monthly'}>1/2 Monthly</option>
                            <option value={''}>None</option>
                        </Select>
                    </Col>
                </Row>
            </Container>

            {!plan.isPrivate && (
                <Select
                    label="List Price"
                    explanation="This is the price displayed in search results in SpotSync Connect"
                    {...register( "planPriceId", { setValueAs: v => v === "" ? null : parseInt( v ) } )}>
                    <option value="">Unlisted</option>
                    {prices?.map( price => (
                        <option key={price.planPriceId}
                                value={price.planPriceId}>
                            {showMoney( price.price )}
                        </option>
                    ) )}
                </Select>
            )}

            <div className="border rounded p-3 mb-3">
                <div className="d-flex align-items-center gap-2 gap-md-4 flex-wrap">
                    <Form.Check
                        type="switch"
                        id="renewalFailureEnabled"
                        label="Deactivate subscription after failed payments"
                        checked={isRenewalEnabled}
                        onChange={(e) => {
                            if (e.target.checked) {
                                setValue("renewalFailureDeactivateAfterDays", 7);
                            } else {
                                setValue("renewalFailureDeactivateAfterDays", 0);
                                clearErrors("renewalFailureDeactivateAfterDays");
                            }
                        }}
                    />
                    <Form.Group className="mb-0 d-flex align-items-center gap-2 ps-2 ps-md-3 border-start border-secondary">
                        <Form.Label htmlFor="renewalFailureDays" className="mb-0 text-nowrap">Days</Form.Label>
                        <Form.Control
                            id="renewalFailureDays"
                            type="number"
                            min={1}
                            disabled={!isRenewalEnabled}
                            className="text-end"
                            style={{ width: "4.5rem" }}
                            {...register("renewalFailureDeactivateAfterDays", {
                                valueAsNumber: true,
                                validate: (v) => v === 0 || v >= 4 || "Must be at least 4 day when enabled"
                            })}
                            isInvalid={!!errors.renewalFailureDeactivateAfterDays}
                        />
                    </Form.Group>
                </div>
                <Form.Text className="d-block text-muted ms-1 mt-1">
                    {!isRenewalEnabled
                        ? "After 3 failed daily payment attempts, the subscription remain active. Specify a grace period above to deactivate the subscription."
                        : "After 3 failed daily payment attempts, the subscription will be deactivated this many days after the start of the billing period. The account will receive an invoice for the balance due."}
                </Form.Text>
                {errors.renewalFailureDeactivateAfterDays && (
                    <Form.Text className="d-block text-danger ms-1">
                        {errors.renewalFailureDeactivateAfterDays.message}
                    </Form.Text>
                )}

                {isRenewalEnabled && (
                    <Alert variant="info" className="alert-light-bg mb-0 mt-2">
                        <b>What this does:</b><br />
                        After 3 failed attempts to charge the card, the subscription
                        will be deactivated and invoiced {" "} 
                        <b>{watch("renewalFailureDeactivateAfterDays")} day(s)</b>{" "}
                        after the start of the billing period.
                    </Alert>
                )}
            </div>

            <Input
                type="text"
                label="Change Note"
                explanation="The reason for the change"
                as="textarea"
                rows={2}
                maxLength={512}
                {...register( "note" )}
                disabled={!hasChanges}
                error={errors.note}
            />

            <div className="d-flex justify-content-end gap-2 mb-3">
                <Button type="button" className="w-50" loading={loading} onClick={ () => nav( `/facility/${facilityId}/plan/${planId}` )}>
                    Cancel                     
                </Button>
                <Button type="submit" className="w-50 btn-main" loading={loading} disabled={!hasChanges}>
                    Save Changes
                </Button>
            </div>
        </form>
    </MediumPage>;
}